import { useCallback, useMemo, useState } from "react"
import { useDispatch } from "react-redux"

import { resetBorrowFlow } from "#/redux-state"
import { ClaimRewardsModalBody } from "#/shared/components/modals/ClaimRewards"
import { ENABLE_MAINNET_MATS_REWARDS } from "#/shared/constants"
import {
  useBorrowTransaction,
  useCanClaimMatsForNextBorrow,
  useClaimMatsForBorrow,
} from "#/shared/hooks"
import { MezoModalProps } from "#/shared/types"
import { separateThousandsWithComma } from "#/shared/utils"
import { ComponentStepProps, StepperModal } from "#/ui/Modal"

import BorrowForm from "./BorrowForm"
import BorrowReview from "./BorrowReview"
import BorrowTransaction from "./BorrowTransaction"

export default function FirstBorrowModal({
  onClose,
  ...props
}: MezoModalProps) {
  const dispatch = useDispatch()

  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false)

  const { borrowHandler, hasError, isLoading } = useBorrowTransaction()

  const { claim, isClaiming, hasClaimed, totalMats } = useClaimMatsForBorrow()

  const { canClaimMatsForBorrow } = useCanClaimMatsForNextBorrow()

  const handleClose = useCallback(() => {
    dispatch(resetBorrowFlow())
    onClose()
  }, [dispatch, onClose])

  const transactionStepTitle = useMemo(() => {
    if (hasError) return "Sign transaction error"
    if (!isLoading) return "Succesfully borrowed"

    return "Sign transaction"
  }, [hasError, isLoading])

  const steps = useMemo(() => {
    const borrowForm = {
      title: "Borrow",
      withCloseButton: true,
      component: BorrowForm,
    }

    const borrowReview = {
      title: "Review borrow",
      component: (stepProps: ComponentStepProps) => (
        <BorrowReview
          {...stepProps}
          handleBorrow={borrowHandler}
          hasAcceptedTerms={hasAcceptedTerms}
          setHasAcceptedTerms={setHasAcceptedTerms}
        />
      ),
    }

    const borrowTransaction = {
      title: transactionStepTitle,
      withCloseButton: false,
      component: (stepProps: ComponentStepProps) => (
        <BorrowTransaction
          {...stepProps}
          hasError={hasError}
          isLoading={isLoading}
          onClaimRewards={claim}
          canClaimRewards={canClaimMatsForBorrow}
        />
      ),
    }

    const borrowClaimMats = {
      component: (stepProps: ComponentStepProps) => (
        <ClaimRewardsModalBody
          {...stepProps}
          isClaiming={isClaiming}
          hasClaimed={hasClaimed}
          totalMats={separateThousandsWithComma(totalMats, 0, 0)}
          nextStepButtonLabel="View borrow"
        />
      ),
    }

    return canClaimMatsForBorrow && ENABLE_MAINNET_MATS_REWARDS
      ? [borrowForm, borrowReview, borrowTransaction, borrowClaimMats]
      : [borrowForm, borrowReview, borrowTransaction]
  }, [
    borrowHandler,
    canClaimMatsForBorrow,
    claim,
    hasAcceptedTerms,
    hasClaimed,
    hasError,
    isClaiming,
    isLoading,
    totalMats,
    transactionStepTitle,
  ])

  return (
    <StepperModal
      {...props}
      variant="tall"
      onClose={handleClose}
      steps={steps}
    />
  )
}
